import _ from "lodash";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, Input, Row } from "reactstrap";
import { FilterSelect, ServiceThreadSpinner } from ".";
import { DesktopContainer } from "../layouts";
import { api, dateHelpers, filterHelpers, UserContext } from "../utils";

export default function RepairWorkOrder(props) {
   const navigate = useNavigate();
   const { id } = useParams();
   const { currentUser } = useContext(UserContext);
   const ALL_OPTION = { value: 0, label: "N/A" };
   const NA_OPTION = { value: "N/A", label: "N/A" };
   const CHOOSE_SIDE_OPTION = { value: "", label: "-- Select --" };
   const SIDE_OPTIONS = [{ value: "A", label: "A" }, { value: "B", label: "B" }];
   const ALL_PROBLEMS = { value: 0, label: "-- Select --" };
   const ALL_MACHINES = {
      value: 0,
      label: "-- Select --",
      activeMachines: "0",
      spindles: 0,
      sides: false,
      sendMail: false,
      machineSpindles: [],
   };
   const [data, setData] = useState(null);
   const [complete, setClosed] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [noEdit, setDisabled] = useState(false);
   const [problems, setProblems] = useState([]);
   const [problemSubs, setProblemSubs] = useState([]); // problem children filtered by parent problem id
   const [problemTemps, setProblemTemps] = useState([]); // problem children (subs) unfiltered
   const [machines, setMachines] = useState([]);
   const [machineNums, setMachineNums] = useState([]);
   const [sides, setSides] = useState([]);
   const [spindles, setSpindles] = useState([]);
   const [filters, setFilters] = useState([
      { filterName: "machineId", value: ALL_MACHINES },
      { filterName: "machineNum", value: ALL_OPTION },
      { filterName: "spindle", value: ALL_OPTION },
      { filterName: "side", value: NA_OPTION },
      { filterName: "problemId", value: ALL_PROBLEMS },
      { filterName: "problemSubId", value: ALL_OPTION },
   ]);

   useEffect(() => {
      // Get possible drop-down options
      if (!currentUser) return;
      var active = false; // Indicator of if we only show active problems and their children
      api
         .fetch(`Repair/LoadRwoDropdowns/${active}`)
         .then((response) => {
            if (response.data.success) {
               const machineList = _.concat(
                  [ALL_MACHINES],
                  _.map(response.data.machines, (m) => ({
                     value: m.machineId,
                     label: m.machineName,
                     activeMachines: m.activeMachines,
                     spindles: m.spindlesPerSide,
                     sides: m.hasBSide,
                     sendMail: m.sendMail,
                     machineSpindles: m.machineSpindles,
                  }))
               );
               const problemList = _.concat(
                  [ALL_PROBLEMS],
                  _.map(response.data.problems, (pblm) => ({
                     value: pblm.problemId,
                     label: pblm.problem,
                  }))
               );
               const problemSubList = _.concat(
                  _.map(response.data.problemSubs, (pSub) => ({
                     value: pSub.id,
                     label: pSub.problem,
                     parent: pSub.problemId,
                  }))
               );
               return {
                  machineList: machineList,
                  problemList: problemList,
                  problemSubList: problemSubList,
               };
            } else {
               throw new Error(response);
            }
         })
         .then(({ machineList, problemList, problemSubList }) => {
            setMachines(machineList);
            setProblems(problemList);
            setProblemTemps(problemSubList);
            // These should only fill in after a machine is selected, just put in a default selections
            setMachineNums([ALL_OPTION]);
            setSides([NA_OPTION]);
            setSpindles([ALL_OPTION]);
            // This should only fill in after a parent problem is selected
            setProblemSubs({ value: 0, label: "N/A", parent: 0 });
            runQuery();
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser]);

   useEffect(() => {
      async function setFields() {
         if (!loaded && data) {
            if (data.workOrderId > 0) {
               // populate child drop-downs as appropriate for the selected rwo machine
               const mach = _.find(machines, (x) => x.value === data.machineTypeId);
               let newMachList = await fixMachines(mach.activeMachines);
               let newSpindleList = await fixSpindles(spindlesFor(mach, data.machineNumber), data.spindle);
               let newSideList = await fixSides(mach.sides, data.spindle, data.machineSide);
               let newSubList = await fixProblemSubs(data.problemId);

               // Set ddl selected values based on the rwo being edited
               const machFilter = data.machineTypeId > 0
                  ? _.find(machines, (m) => m.value === data.machineTypeId)
                  : ALL_MACHINES;
               let newFilters = filterHelpers.getFilters(filters, {
                  filterName: "machineId",
                  value: machFilter,
               });

               const numFilter = data.machineNumber > 0
                  ? _.find(newMachList, (m) => m.value === data.machineNumber)
                  : ALL_OPTION;
               newFilters = filterHelpers.getFilters(newFilters, {
                  filterName: "machineNum",
                  value: numFilter,
               });

               const spindleFilter = data.spindle > 0
                  ? _.find(newSpindleList, (s) => s.value === data.spindle)
                  : ALL_OPTION;
               newFilters = filterHelpers.getFilters(newFilters, {
                  filterName: "spindle",
                  value: spindleFilter,
               });

               const sideFilter = data.machineSide !== NA_OPTION.value
                  ? _.find(newSideList, (s) => s.value === data.machineSide) || NA_OPTION
                  : NA_OPTION;
               newFilters = filterHelpers.getFilters(newFilters, {
                  filterName: "side",
                  value: sideFilter,
               });

               const pblmFilter = data.problemId > 0
                  ? _.find(problems, (p) => p.value === data.problemId)
                  : ALL_PROBLEMS;
               newFilters = filterHelpers.getFilters(newFilters, {
                  filterName: "problemId",
                  value: pblmFilter,
               });

               const subFilter = data.problemSubId && data.problemSubId > 0
                  ? _.find(newSubList, (p) => p.value === data.problemSubId)
                  : ALL_OPTION;
               newFilters = filterHelpers.getFilters(newFilters, {
                  filterName: "problemSubId",
                  value: subFilter,
               });

               setFilters(newFilters);
            }

            setClosed(data.closed);
            setLoaded(true);
         }
      }
      setFields();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data]);

   function runQuery() {
      api
         .post(`Repair/GetRepairWorkOrder/${id}`)
         .then((response) => {
            if (response.data.success) {
               setDisabled(!response.data.canEdit && id > 0);
               setData(response.data.rwo);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
   }

   function onChange(field, value) {
      let changes = Object.assign({}, data);
      changes[field] = value;
      setData(changes);
   }

   // These are related to the machine type selection change and its sub filters
   const onMachineChange = async (newFilterValue) => {
      // Fill in the machine related drop-down lists with proper values
      const mach = _.find(machines, (x) => x.value === newFilterValue.value);
      let newMachList = await fixMachines(mach.activeMachines);
      let newSpindleList = await fixSpindles(spindlesFor(mach, ALL_OPTION.value));
      let newSideList = await fixSides(mach.sides, ALL_OPTION.value);

      // Set the machine dependent filters to their defaults
      const numFilter = _.find(newMachList, (m) => m.value === 0);
      const spindleFilter = _.find(newSpindleList, (s) => s.value === 0);
      const sideFilter = _.find(newSideList, (s) => s.value === NA_OPTION.value);

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "machineId",
         value: mach,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "machineNum",
         value: numFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "spindle",
         value: spindleFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "side",
         value: sideFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, data);
      changes.machineTypeId = newFilterValue.value;
      changes.machineName = mach.label;
      changes.machineNumber = ALL_OPTION.value;
      changes.machineSide = NA_OPTION.value;
      changes.spindle = ALL_OPTION.value;
      setData(changes);
   };

   const fixMachines = async (activeMachines) => {
      // Fill in the drop-down with active machines for the selected machine
      var nums = activeMachines.split(", ");
      let newMachineList = _.concat(
         [ALL_OPTION],
         _.map(nums, (m) => ({ value: parseInt(m, 10), label: m }))
      );
      setMachineNums(newMachineList);
      return newMachineList;
   };

   // Spindles on one side of the machine: its own count when the server sent one, else the machine type's.
   const spindlesFor = (mach, machineNumber) => {
      const own = _.find(mach.machineSpindles, (m) => m.machineNumber === machineNumber);
      return own ? own.spindlesPerSide : mach.spindles;
   };

   const fixSpindles = async (spindlesPerSide, savedSpindle = 0) => {
      // Fill in the drop-down with available spindles for the selected machine
      let newSpindleList = [ALL_OPTION];
      for (let i = 1; i <= spindlesPerSide; i++) {
         newSpindleList = _.concat(
            newSpindleList,
            _.map([{ value: i, label: i.toString() }])
         );
      }
      // A saved spindle past the count (a scanned label carries the printed number) must stay selectable.
      if (savedSpindle > spindlesPerSide) {
         newSpindleList = _.concat(newSpindleList, [{ value: savedSpindle, label: savedSpindle.toString() }]);
      }
      setSpindles(newSpindleList);
      return newSpindleList;
   };

   const fixSides = async (hasBSide, spindle, savedSide) => {
      // Fill in the drop-down with available sides for the selected machine and spindle:
      // a spindle on a two-sided machine needs A or B (N/A is the whole machine); a one-sided machine is N/A.
      let newSideList = [NA_OPTION];
      if (hasBSide === true && spindle > 0) {
         newSideList = [CHOOSE_SIDE_OPTION, ...SIDE_OPTIONS];
      }
      // An older RWO saved before this rule may hold a side the rule no longer offers - keep it visible and selectable,
      // as fixSpindles keeps a saved out-of-range spindle. Only the load passes savedSide.
      if (savedSide && !_.some(newSideList, (s) => s.value === savedSide)) {
         newSideList = _.concat(newSideList, [{ value: savedSide, label: savedSide }]);
      }
      setSides(newSideList);
      return newSideList;
   };

   // These are related to the problem type selection change and its sub filter
   const onProblemChange = async (newFilterValue) => {
      // Fill in the problem child drop-down with proper values
      const pblm = _.find(problems, (x) => x.value === newFilterValue.value);
      let newSubList = await fixProblemSubs(newFilterValue.value);

      // Set the problem filters
      var subs = _.pickBy(problemTemps, (e) =>
         _.includes([newFilterValue.value], e.parent)
      );
      const subFilter = _.find(newSubList, (s) => s.value === 0);

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "problemId",
         value: pblm,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "problemSubId",
         value: subFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, data);
      changes.problemId = newFilterValue.value;
      changes.problem = pblm.label;
      changes.problemSubId = subs ? ALL_PROBLEMS.value : ALL_OPTION.value;
      changes.problemChild = "";
      setData(changes);
   };

   const fixProblemSubs = async (id) => {
      let newPblmList = [ALL_OPTION]; // If no children found then N/A is the default option
      var subs = _.pickBy(problemTemps, (e) => _.includes([id], e.parent));
      if (subs) {
         // There are child problems for the selected problem type
         newPblmList = _.concat(
            [ALL_PROBLEMS],
            _.map(subs, (s) => ({ value: s.value, label: s.label }))
         );
         setProblemSubs(newPblmList);
      }
      return newPblmList;
   };

   // These are selection changes without sub filters
   const onMachineNumChange = async (newFilterValue) => {
      // Each machine has its own spindle count, so the spindle (and with it the side) is chosen again
      const mach = _.find(machines, (x) => x.value === data.machineTypeId);
      let newSpindleList = await fixSpindles(spindlesFor(mach, newFilterValue.value));
      let newSideList = await fixSides(mach.sides, ALL_OPTION.value);

      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "spindle",
         value: _.find(newSpindleList, (s) => s.value === 0),
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "side",
         value: _.find(newSideList, (s) => s.value === NA_OPTION.value),
      });
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.machineNumber = newFilterValue.value;
      changes.spindle = ALL_OPTION.value;
      changes.machineSide = NA_OPTION.value;
      setData(changes);
   };

   function onSideChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.machineSide = newFilterValue.value;
      setData(changes);
   }

   const onSpindleChange = async (newFilterValue) => {
      // The side choices follow the spindle: keep the side when still offered, else choose again (A/B) or N/A
      const mach = _.find(machines, (x) => x.value === data.machineTypeId);
      let newSideList = await fixSides(mach.sides, newFilterValue.value);
      const keptSide = _.find(newSideList, (s) => s.value === data.machineSide) || newSideList[0];

      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "side",
         value: keptSide,
      });
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.spindle = newFilterValue.value;
      changes.machineSide = keptSide.value;
      setData(changes);
   };

   function onProblemSubChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      const pblm = _.find(problemSubs, (x) => x.value === newFilterValue.value);
      let changes = Object.assign({}, data);
      changes.problemSubId = newFilterValue.value;
      changes.problemChild = pblm.label;
      setData(changes);
   }

   // Save changes to database
   function saveWorkOrder() {
      let warnings = [];

      if (noEdit && id > 0) {
         warnings.push("You are not authorized to make changes\n");
      }
      if (complete && !currentUser.isAdmin) {
         warnings.push("The rwo is already complete and cannot be changed\n");
      }
      if (!data.machineTypeId) {
         warnings.push("Machine is required\n");
      }
      if (!data.machineNumber) {
         warnings.push("Machine # is required\n");
      }
      if (!data.machineSide) {
         warnings.push("Side is required\n");
      }
      //if (!data.spindle && spindles.length > 1) {
      //  warnings.push('Spindle is required\n');
      //}
      if (!data.problemId) {
         warnings.push("Problem is required\n");
      }
      if (data.problemId && problemSubs.length > 1 && !data.problemSubId) {
         warnings.push("Child problem is required\n");
      }
      if (!data.operatorInitials) {
         warnings.push("Lead person initials are required\n");
      }
      if (warnings.length) {
         toast.error(warnings.join(''));
         return;
      }

      api
         .post(`Repair/SaveRepairWorkOrder`, data)
         .then((response) => {
            if (response.data.success) {
               if (data.workOrderId === 0)
                  toast.success("New work order was created successfully!");
               else toast.success("Update Successful!");

               // Saved, but the maintenance email failed - say so, so nobody re-submits a saved RWO
               if (response.data.emailWarning)
                  toast.warning(response.data.emailWarning, { autoClose: false });

               // setData(response.data.rwo);
               navigate(`/rwo-search`);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   return (
      <DesktopContainer screenName="Repair Work Order">
         <ServiceThreadSpinner on={!loaded}>
            <div className="filter-header">
               <Row className="justify-content-center">
                  <Col xs={7}>
                     <Row>
                        <Col xs={3}>
                           <h5 className="text-end text-white h5">Work Order #:</h5>
                        </Col>
                        <Col xs={4}>
                           <h5 className="text-white">
                              {data ? `${data.displayId}` : "Determined upon save"}
                           </h5>
                        </Col>
                     </Row>
                     <Row>
                        <Col xs={3}>
                           <h5 className="text-end text-white h5">Entered Date:</h5>
                        </Col>
                        <Col xs={4}>
                           <h5 className="text-white">
                              {data && data.workOrderId > 0
                                 ? dateHelpers.dateTimeFormat(
                                    data.enteredDate,
                                    "MM/DD/YYYY HH:mm"
                                 )
                                 : null}
                           </h5>
                        </Col>
                     </Row>
                     <br />
                     <Row>
                        <Col className="text-end text-white h5" xs={3}>
                           Machine:
                        </Col>
                        {/* Long-valued selects span the field and spare columns; short ones (machine #, side, spindle) stay xs={4} */}
                        <Col xs={9}>
                           <FilterSelect
                              filterName="machineId"
                              options={machines}
                              value={filterHelpers.getValue(filters, "machineId")}
                              onChangeCallback={(e) => onMachineChange(e)}
                           />
                        </Col>
                     </Row>
                     <br />
                     <Row>
                        <Col className="text-end text-white h5" xs={3}>
                           Machine #:
                        </Col>
                        <Col xs={4}>
                           <FilterSelect
                              filterName="machineNum"
                              options={machineNums}
                              value={filterHelpers.getValue(filters, "machineNum")}
                              onChangeCallback={(e) => onMachineNumChange(e)}
                           />
                        </Col>
                     </Row>
                     <br />
                     <Row>
                        <Col className="text-end text-white h5" xs={3}>
                           Side:
                        </Col>
                        <Col xs={4}>
                           <FilterSelect
                              filterName="side"
                              options={sides}
                              value={filterHelpers.getValue(filters, "side")}
                              onChangeCallback={(e) => onSideChange(e)}
                           />
                        </Col>
                     </Row>
                     <br />
                     <Row>
                        <Col className="text-end text-white h5" xs={3}>
                           Spindle #:
                        </Col>
                        <Col xs={4}>
                           <FilterSelect
                              filterName="spindle"
                              options={spindles}
                              value={filterHelpers.getValue(filters, "spindle")}
                              onChangeCallback={(e) => onSpindleChange(e)}
                           />
                        </Col>
                     </Row>
                     <br />
                     <Row>
                        <Col className="text-end text-white h5" xs={3}>
                           Problem Type:
                        </Col>
                        <Col xs={9}>
                           <FilterSelect
                              filterName="problemId"
                              options={problems}
                              value={filterHelpers.getValue(filters, "problemId")}
                              onChangeCallback={(e) => onProblemChange(e)}
                           />
                        </Col>
                     </Row>
                     {problemSubs && problemSubs.length >= 1 ? (
                        <div>
                           <br />
                           <Row>
                              <Col className="text-end text-white h5" xs={3}>
                                 Specific Issue:
                              </Col>
                              <Col xs={9}>
                                 <FilterSelect
                                    filterName="problemSubId"
                                    options={problemSubs}
                                    value={filterHelpers.getValue(filters, "problemSubId")}
                                    onChangeCallback={(e) => onProblemSubChange(e)}
                                 />
                              </Col>
                           </Row>
                        </div>
                     ) : null}
                     <br />
                     <Row>
                        <Col xs={3}>
                           <h5 className="text-end text-white h5">
                              Additional Comments:
                           </h5>
                        </Col>
                        <Col xs={4}>
                           <Input
                              type="textarea"
                              className="serviceThreadTextArea"
                              value={(data && data.comments) ?? ""}
                              maxLength={500}
                              onChange={(e) => {
                                 onChange("comments", e.target.value);
                              }}
                           />
                        </Col>
                     </Row>
                     <br />
                     {/*Hidden at Jay's Request*/}
                     {/* <Row>
                <Col xs="3">
                  <h5 className="text-end text-white h5">Lead Initials:</h5>
                </Col>
                <Col xs="1">
                  <Input
                    type="text"
                    maxLength={2}
                    value={(data && data.operatorInitials) ?? ''}
                    onChange={(e) => { onChange("operatorInitials", e.target.value) }}
                  />
                </Col>
              </Row> */}
                     {data && data.workOrderId > 0 ? (
                        <Fragment>
                           <br />
                           <Row>
                              <Col xs={7}>
                                 <h2 className="text-white">Mechanics Comments</h2>
                              </Col>
                           </Row>
                           <Row>
                              <Col xs={7}>
                                 <hr />
                              </Col>
                           </Row>
                           <Row>
                              <Col xs={3}>
                                 <h5 className="text-end text-white h5">Repair Date:</h5>
                              </Col>
                              <Col xs={4}>
                                 <h5 className="text-white">
                                    {data
                                       ? dateHelpers.dateTimeFormat(
                                          data.repairDate,
                                          "MM/DD/YYYY HH:mm"
                                       )
                                       : null}
                                 </h5>
                              </Col>
                           </Row>
                           <br />
                           <Row>
                              <Col xs={3}>
                                 <h5 className="text-end text-white h5">
                                    Repair Comments:
                                 </h5>
                              </Col>
                              <Col xs={4}>
                                 <Input
                                    type="textarea"
                                    className="serviceThreadTextArea"
                                    value={
                                       data && data.repairComments ? data.repairComments : ""
                                    }
                                    maxLength={500}
                                    onChange={(e) => {
                                       onChange("repairComments", e.target.value);
                                    }}
                                 />
                              </Col>
                           </Row>
                           <br />
                           {/*Hidden at Jay's Request*/}
                           {/* <Row>
                      <Col xs="3">
                        <h5 className="text-end text-white h5">Tech Initials:</h5>
                      </Col>
                      <Col xs="1">
                        <Input
                          type="text"
                          maxLength={2}
                          value={data && data.mechanicInitials ? data.mechanicInitials : ''}
                          onChange={(e) => { onChange("mechanicInitials", e.target.value) }}
                        />
                      </Col>
                    </Row> */}
                           <br />
                           <Row>
                              <Col xs="3">
                                 <h5 className="text-end text-white h5">
                                    Repair Complete?
                                 </h5>
                              </Col>
                              <Col xs="4">
                                 &nbsp;
                                 <Input
                                    type="checkbox"
                                    checked={data && data.closed}
                                    disabled={noEdit}
                                    onChange={(e) => {
                                       onChange("closed", e.target.checked);
                                    }}
                                 />
                              </Col>
                           </Row>
                        </Fragment>
                     ) : null}

                     <br />
                     <div>
                        <Row>
                           <Col xs="7">
                              <hr />
                           </Col>
                        </Row>
                        <Row>
                           <Col xs="3">
                              <Button
                                 title="Return To Search"
                                 className="shredButton ms-2"
                                 onClick={() => navigate(`/rwo-search`)}
                              >
                                 Return To Search
                              </Button>
                           </Col>
                           {!noEdit ? (
                              <Col xs="4">
                                 <Button
                                    title="Save"
                                    className="shredButton float-end ms-2"
                                    disabled={noEdit}
                                    onClick={() => saveWorkOrder()}
                                 >
                                    Save
                                 </Button>
                              </Col>
                           ) : null}
                        </Row>
                     </div>
                  </Col>
               </Row>
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
